import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const DRAWER_MS = 300;

/**
 * Cart drawer — opens from cart icon only; closes via X, overlay, or Escape.
 * Survives cart quantity/remove morphs without closing or freezing the page.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {boolean} */
  #openedByUser = false;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {boolean} */
  #bound = false;

  /** @type {number | undefined} */
  #openFrame = undefined;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.#bound) return;
    this.#bound = true;

    const dialog = this.querySelector('dialog');
    if (dialog) {
      this.#guardNativeOpen(dialog);
      dialog.addEventListener('toggle', this.#onDialogToggle);
      dialog.addEventListener('cancel', this.#onDialogCancel);
      dialog.addEventListener('close', this.#onDialogClose);
    }

    // Delegation survives morph after qty/remove updates
    this.addEventListener('click', this.#onComponentClick);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#blockThirdPartyOpeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);

    this.removeEventListener('click', this.#onComponentClick);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    const dialog = this.refs?.dialog;
    if (dialog) {
      dialog.removeEventListener('toggle', this.#onDialogToggle);
      dialog.removeEventListener('cancel', this.#onDialogCancel);
      dialog.removeEventListener('close', this.#onDialogClose);
    }

    if (this.#openedByUser || document.documentElement.hasAttribute('scroll-lock')) {
      const otherOpen = document.querySelector('cart-drawer-component dialog[open]');
      if (!otherOpen) {
        document.documentElement.removeAttribute('scroll-lock');
      }
    }

    this.#bound = false;
  }

  /**
   * After qty/remove, section morph can strip classes / break modal state.
   */
  #onCartUpdate = () => {
    if (!this.#openedByUser || this.#isClosing) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#restoreAfterMorph();
      });
    });
  };

  #restoreAfterMorph() {
    const dialog = this.querySelector('dialog') || this.refs?.dialog;
    if (!dialog || !this.#openedByUser || this.#isClosing) return;

    this.#guardNativeOpen(dialog);
    dialog.classList.remove('is-opening', 'is-closing');

    if (dialog.open && !dialog.matches(':modal')) {
      try {
        dialog.close();
      } catch (_) {
        /* ignore */
      }
      this.#openedByUser = true;
      dialog.showModal();
    } else if (!dialog.open) {
      this.#openedByUser = true;
      dialog.showModal();
    }

    document.documentElement.setAttribute('scroll-lock', '');
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  #guardNativeOpen(dialog) {
    if (dialog.dataset.cartDrawerGuarded === 'true') return;
    dialog.dataset.cartDrawerGuarded = 'true';

    const nativeShowModal = HTMLDialogElement.prototype.showModal.bind(dialog);
    const nativeShow = HTMLDialogElement.prototype.show.bind(dialog);

    dialog.showModal = () => {
      if (!this.#openedByUser) return;
      return nativeShowModal();
    };

    dialog.show = () => {
      if (!this.#openedByUser) return;
      return nativeShow();
    };
  }

  #blockThirdPartyOpeners() {
    try {
      window.openCartDrawer = () => false;
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onComponentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (trigger && this.contains(trigger)) {
      event.preventDefault();
      event.stopPropagation();
      this.openDrawer();
      return;
    }

    const closeBtn = event.target.closest('[data-cart-drawer-close]');
    if (closeBtn && this.contains(closeBtn)) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDrawer();
      return;
    }

    // Overlay only — never treat qty +/- / remove as outside clicks
    if (this.#openedByUser && event.target === this.refs.dialog) {
      this.closeDrawer();
    }
  };

  /** @param {Event} event */
  #onDialogCancel = (event) => {
    event.preventDefault();
    this.closeDrawer();
  };

  #onDialogToggle = () => {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog) return;

    if (dialog.open && !this.#openedByUser && !this.#isClosing) {
      queueMicrotask(() => {
        if (dialog.open && !this.#openedByUser) dialog.close();
      });
    }
  };

  #onDialogClose = () => {
    // Morph may briefly close the dialog; keep intent if user still has it open
    if (this.#isClosing) {
      this.#openedByUser = false;
      this.#isClosing = false;
      this.refs.dialog?.classList.remove('is-opening', 'is-closing');
    }
  };

  /**
   * Opens after add-to-cart when theme auto-open is on, or when the add event
   * requests it (e.g. buy-now when cart type is drawer).
   * @param {CustomEvent} event
   */
  #handleCartAdd = (event) => {
    const data = event?.detail?.data || {};
    if (this.hasAttribute('auto-open') || data.openCartDrawer) {
      this.openDrawer();
    }
  };

  openDrawer() {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog || this.#isClosing) return;

    if (dialog.open) {
      dialog.classList.remove('is-opening', 'is-closing');
      document.documentElement.setAttribute('scroll-lock', '');
      return;
    }

    this.#openedByUser = true;
    dialog.classList.remove('is-closing');
    dialog.classList.add('is-opening');
    dialog.showModal();

    if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = requestAnimationFrame(() => {
        dialog.classList.remove('is-opening');
        this.#openFrame = undefined;
      });
    });

    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDrawer() {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog?.open || this.#isClosing) return;

    this.#isClosing = true;
    dialog.classList.add('is-closing');
    dialog.classList.remove('is-opening');

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;

      dialog.removeEventListener('transitionend', onEnd);
      if (this.#closeTimer) clearTimeout(this.#closeTimer);

      this.#openedByUser = false;
      this.#isClosing = false;

      if (dialog.open) {
        dialog.close();
      }

      dialog.classList.remove('is-closing', 'is-opening');
      document.documentElement.removeAttribute('scroll-lock');
      this.dispatchEvent(new DialogCloseEvent());
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== dialog || event.propertyName !== 'transform') return;
      finish();
    };

    dialog.addEventListener('transitionend', onEnd);
    this.#closeTimer = setTimeout(finish, DRAWER_MS + 50);
  }

  open() {}

  showDialog() {}

  close() {
    this.closeDrawer();
  }

  closeDialog = async () => {
    this.closeDrawer();
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
